import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import { periodLabel } from '~/constants/strategyConstants';
import i18n from '~/i18n';

// Backend timestamps are "YYYY-MM-DD HH:MM:SS" — show "YYYY-MM-DD HH:MM". Kept as plain
// string slicing (no Date/Intl) so it renders the backend's own timezone unchanged. Guards
// against non-string input — the engine's no-data sentinel puts -99999 (a number) in the
// trade date arrays.
export const formatStrategyDateTime = (raw: unknown): string => {
    if (typeof raw !== 'string' || !raw) {
        return '';
    }
    const [datePart, timePart] = raw.split(' ');
    if (!timePart) {
        return datePart;
    }
    return `${datePart} ${timePart.slice(0, 5)}`;
};

// Date portion only ("YYYY-MM-DD") — list rows show when a strategy was created without
// the time-of-day noise; the detail identity strip keeps the full timestamp.
export const formatStrategyDate = (raw: unknown): string => formatStrategyDateTime(raw).split(' ')[0] ?? '';

// Side, TP/SL and the indicator names all come from `frontend_json` — this app writes them
// there on create, and since 2026-07-26 the backend has no columns of its own for them
// (docs/ai-strategy/08-backend-api-changes.md). Strategies created before that, or by an app
// build that didn't send the blob, simply carry less: every helper below degrades gracefully.

// "Gold · Buy · 1 hour" — the compact market identity, used where TP/SL aren't wanted
// (the quick-actions sheet title, the detail nav header). `instrumentName` comes from the
// caller's symbols lookup (falls back to the raw code there).
export const strategyMarketLine = (strategy: Strategy, instrumentName: string): string => {
    const side = strategy.frontend_json?.side;
    return [instrumentName, side ? i18n.t(side) : null, periodLabel(strategy.instrument_period)]
        .filter(Boolean)
        .join(' · ');
};

// A distance is only worth showing when it's a real number and non-zero: the backfilled
// strategies carry takeProfit/stopLoss 0.0, which would render a meaningless "TP 0 · SL 0".
const cleanDistance = (v: unknown): string | undefined =>
    typeof v === 'number' && Number.isFinite(v) && v !== 0 ? String(v) : undefined;

export const strategyTakeProfitStopLoss = (strategy: Strategy): { tp?: string; sl?: string } => ({
    tp: cleanDistance(strategy.frontend_json?.takeProfit),
    sl: cleanDistance(strategy.frontend_json?.stopLoss),
});

// "Gold · Buy · 1 hour · TP 30 · SL 5" — the row/detail title.
export const strategyTitleLine = (strategy: Strategy, instrumentName: string): string => {
    const { tp, sl } = strategyTakeProfitStopLoss(strategy);
    return [strategyMarketLine(strategy, instrumentName), tp ? `TP ${tp}` : null, sl ? `SL ${sl}` : null]
        .filter(Boolean)
        .join(' · ');
};

// "EMA short | EMA long" — the subtitle. These are the wizard's own display labels, stored at
// creation, so they read the same here as they did in the rules step.
export const strategyIndicatorLine = (strategy: Strategy): string =>
    (strategy.frontend_json?.indicators ?? []).filter(Boolean).join(' | ');

const THIRTY_DAYS_MS = 30 * 24 * 3600 * 1000;

// Where the Live lists start their date range. `created` is "YYYY-MM-DD HH:MM:SS", which JSC and
// Safari will not parse as-is, hence the 'T'. Falls back to the reports' usual 30-day window
// when the strategy carries no usable date. Ideally this would be `enabled_at` — see doc 09;
// the backend has not added it, so creation is the closest available proxy.
export const strategyStartDate = (strategy: Strategy): Date => {
    const parsed = typeof strategy.created === 'string' ? new Date(strategy.created.replace(' ', 'T')) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) {
        return new Date(Date.now() - THIRTY_DAYS_MS);
    }
    return parsed;
};
